import { useAuth } from '@/context/AuthContext'
import { Avatar, Box, Flex, Stack, Text } from '@chakra-ui/react'
import { faUser } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { UserPagedQuery } from 'BrokerTsLib/src/dtos'
import { useSearchUsers } from 'BrokerTsLib/src/hooks/useUser'
import { Select, type SingleValue } from 'chakra-react-select'
import { type ReactNode, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

const UNASSIGN = '__unassign__'

type Option = {
  value: string          // user id, or UNASSIGN to clear the assignee
  label: string          // display name
  email?: string
  kind: 'me' | 'unassign' | 'user'
}

type AssigneeSelectProps = {
  assigneeId?: string                                       // current assignee, when a single one applies
  assigneeName?: string
  onChange: (assigneeId: string | null) => void | Promise<void>  // null = unassign/clear; caller owns persistence
  isLoading?: boolean
  isDisabled?: boolean
  restPlaceholder?: ReactNode                               // shown at rest; e.g. bulk passes "Přiřadit požadavky"
}

const PersonIcon = () => <Avatar size="sm" bg="gray.100" color="gray.500" icon={<FontAwesomeIcon icon={faUser} />} />

export const AssigneeSelect = ({ assigneeId, assigneeName, onChange, isLoading, isDisabled, restPlaceholder }: AssigneeSelectProps) => {
  const { user } = useAuth()
  const [search, setSearch] = useState<UserPagedQuery>({ pageIndex: 1, pageSize: 20, showInvalid: false })
  const [focused, setFocused] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const { data, isFetching } = useSearchUsers(search)

  // Debounce the server query so typing doesn't fire a /Search request per keystroke (matters with many users).
  const debouncedSearch = useDebouncedCallback(
    (input: string) => setSearch(s => ({ ...s, name: input || undefined })),
    250,
  )

  const meOption: Option[] = user?.id
    ? [{ value: user.id, label: (user.userFullName || `${user.firstName ?? ''} ${user.lastName ?? ''}`).trim() || user.userName || '', email: user.email, kind: 'me' }]
    : []

  const userOptions: Option[] = (data?.data.items ?? [])
    .filter(u => u.userId !== user?.id)
    .map(u => ({ value: u.userId, label: `${u.firstName} ${u.lastName}`.trim() || u.userName, email: u.email, kind: 'user' }))

  // While searching, show only matching users — drop the pinned "me"/"unassign" so they don't pollute results.
  const pinned: Option[] = search.name
    ? []
    : [...meOption, { value: UNASSIGN, label: 'Nepřiděleno', kind: 'unassign' }]

  const options: Option[] = [...pinned, ...userOptions]

  const value: Option | null = assigneeId
    ? { value: assigneeId, label: assigneeName ?? '', kind: 'user' }
    : null

  const handleChange = async (option: SingleValue<Option>) => {
    setInputValue('')
    const newAssigneeId = !option || option.value === UNASSIGN ? null : option.value
    await onChange(newAssigneeId)
  }

  return (
    <Box minW="240px">
      <Select<Option>
        size="sm"
        isLoading={isFetching || isLoading}
        isDisabled={isDisabled || isLoading}
        isClearable
        openMenuOnFocus
        inputValue={inputValue}
        placeholder={focused ? 'Vyhledat…' : (restPlaceholder ?? <Flex align="center" gap={2}><PersonIcon /><Text color="gray.500">Nepřiděleno</Text></Flex>)}
        onFocus={e => {
          setFocused(true)
          // JIRA-style: prefill the current assignee name, pre-selected, so typing replaces it. The menu still
          // shows the full list (server search only fires once you actually type — filterOption is disabled).
          if (assigneeName) {
            const el = e.target
            setInputValue(assigneeName)
            setTimeout(() => el.select())
          }
        }}
        onBlur={() => setFocused(false)}
        options={options}
        value={value}
        onChange={handleChange}
        variant="ghost"
        onInputChange={(input, meta) => {
          if (meta.action === 'input-change') {
            setInputValue(input)
            debouncedSearch(input)
          } else if (meta.action === 'menu-close' || meta.action === 'input-blur') {
            setInputValue('')
            debouncedSearch('')
          }
        }}
        getOptionValue={o => o.value}
        filterOption={() => true}
        formatOptionLabel={(opt, meta) => {
          if (opt.kind === 'unassign') {
            return <Flex align="center" gap={2}><PersonIcon /><Text>{opt.label}</Text></Flex>
          }
          if (meta.context === 'value') {
            return <Flex align="center" gap={2}><Avatar size="sm" colorScheme="primary" name={opt.label} /><Text>{opt.label}</Text></Flex>
          }
          return (
            <Flex align="center" gap={2}>
              <Avatar size="sm" colorScheme="primary" name={opt.label} />
              <Stack gap={0}>
                <Text fontSize="sm" fontWeight="medium">{opt.label}{opt.kind === 'me' ? ' (Přidělit mně)' : ''}</Text>
                {opt.email && <Text fontSize="xs" color="gray.500">{opt.email}</Text>}
              </Stack>
            </Flex>
          )
        }}
        chakraStyles={{
          // Look like a clickable label at rest, but reveal a text-input box (border + caret + white bg)
          // on hover/focus so it's obvious you can type to search. Transparent 1px border at rest avoids layout shift.
          control: (provided, state) => ({
            ...provided,
            cursor: 'text',
            minH: '40px', // fixed height so the control doesn't grow when a value (with avatar) is selected
            borderWidth: '1px',
            borderColor: state.isFocused ? 'primary.500' : 'transparent',
            bg: state.isFocused ? 'white' : provided.bg,
            boxShadow: state.isFocused ? 'sm' : 'none',
            _hover: { borderColor: state.isFocused ? 'primary.500' : 'gray.300' },
          }),
          menu: provided => ({ ...provided, zIndex: 3 }),
          inputContainer: provided => ({ ...provided, cursor: 'text', px: 0 })
        }}
      />
    </Box>
  )
}
